import React, { useState, useEffect } from "react";
import SystemAnnouncementsAPI from "./services/SystemAnnouncementsAPI";
import AlertsAPI from "./services/AlertsAPI";
import TrainingAPI from "./services/TrainingAPI";
import { useUser } from "./context/UserContext";
import { showToastWithAction } from "./utils/ToastWithAction";
import {
  formatDateTime,
  formatDateOnly,
  formatTimeOnly,
} from "./utils/DateTimeUtil";
import DateTimeField from "../common/datetime/DateTimeField";
import {
  browserTimeZone,
  instantToWallClock,
  wallClockToNaiveUtc,
} from "../common/datetime/dateTimeParts";
import "./css/AlertManagement.css";
import LoadingSpinner from "./common/LoadingSpinner";

/** Announcement windows are set in whatever zone the admin is sitting in. */
const ALERT_TZ = browserTimeZone();

/**
 * AlertManagement Component - Platform Admin interface for managing System Announcements
 *
 * System Announcements are platform-level notifications like:
 * - Scheduled maintenance notices
 * - New feature announcements
 * - Service status updates
 *
 * This is separate from the Alerts Intelligence Hub which handles scam education.
 */
const AlertManagement = () => {
  const { loggedInUser } = useUser();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state for creating/editing announcements
  const [formData, setFormData] = useState({
    message: "",
    type: "INFO",
    startDate: "",
    endDate: "",
    priority: 50,
    targetAudience: "ALL",
    isActive: true,
    relatedAlertIds: [],
    relatedTrainingUuids: [],
  });

  // Available alerts & trainings for linking
  const [availableAlerts, setAvailableAlerts] = useState([]);
  const [availableTrainings, setAvailableTrainings] = useState([]);

  // Edit mode state
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");

  // Announcement type options
  const announcementTypes = [
    { value: "INFO", label: "Information" },
    { value: "WARNING", label: "Warning" },
    { value: "ERROR", label: "Error" },
    { value: "SUCCESS", label: "Success" },
    { value: "MAINTENANCE", label: "Maintenance" },
  ];

  // Priority options (1-100, higher = more important)
  const priorityOptions = [
    { value: 20, label: "Low (20)" },
    { value: 50, label: "Medium (50)" },
    { value: 70, label: "High (70)" },
    { value: 90, label: "Critical (90)" },
  ];

  // Target audience options
  const audienceOptions = [
    { value: "ALL", label: "All Users" },
    { value: "AUTHENTICATED", label: "Authenticated Users" },
    { value: "ADMIN", label: "Admins Only" },
  ];

  useEffect(() => {
    fetchAnnouncements();
    fetchAvailableAlerts();
    fetchAvailableTrainings();
  }, []);

  /**
   * Fetch available alerts for the linked content picker
   */
  const fetchAvailableAlerts = async () => {
    try {
      const alerts = await AlertsAPI.getAllAlertsForAdmin();
      setAvailableAlerts(Array.isArray(alerts) ? alerts : []);
    } catch (err) {
      console.error("Failed to fetch alerts for picker:", err);
    }
  };

  /**
   * Fetch available trainings for the linked content picker
   */
  const fetchAvailableTrainings = async () => {
    try {
      const trainings = await TrainingAPI.getAllTrainings();
      setAvailableTrainings(Array.isArray(trainings) ? trainings : []);
    } catch (err) {
      console.error("Failed to fetch trainings for picker:", err);
    }
  };

  /**
   * Fetch all announcements for admin management
   */
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("🔄 AlertManagement: Fetching announcements...");
      const response =
        await SystemAnnouncementsAPI.getAllAnnouncementsForAdmin();
      console.log(
        "📊 AlertManagement: API Response:",
        response,
        "Type:",
        typeof response,
        "IsArray:",
        Array.isArray(response),
      );
      setAnnouncements(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("❌ AlertManagement: Error fetching announcements:", err);
      setError(
        "Failed to fetch announcements: " + (err.message || "Unknown error"),
      );
      setAnnouncements([]); // Ensure announcements is always an array even on error
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setFormData({
      message: "",
      type: "INFO",
      startDate: "",
      endDate: "",
      priority: 50,
      targetAudience: "ALL",
      isActive: true,
      relatedAlertIds: [],
      relatedTrainingUuids: [],
    });
    setIsEditing(false);
    setEditingAnnouncement(null);
  };

  /**
   * Handle form submission for creating/updating announcements
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.message.trim()) {
      setError("Announcement message is required");
      return;
    }

    // Validate dates
    if (
      formData.startDate &&
      formData.endDate &&
      formData.startDate >= formData.endDate
    ) {
      setError("End date must be after start date");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build message with optional Alerts Intelligence Hub link
      let finalMessage = formData.message;

      const announcementData = {
        ...formData,
        message: finalMessage,
        priority:
          typeof formData.priority === "number" ? formData.priority : 50,
        targetAudience: mapAudienceToBackend(formData.targetAudience),
        startDate: formatDateForBackend(formData.startDate),
        endDate: formatDateForBackend(formData.endDate),
        relatedAlertIds:
          formData.relatedAlertIds.length > 0 ? formData.relatedAlertIds : null,
        relatedTrainingUuids:
          formData.relatedTrainingUuids.length > 0
            ? formData.relatedTrainingUuids
            : null,
      };

      console.log(
        "🔧 AlertManagement: Sending announcement data:",
        announcementData,
      );

      if (isEditing && editingAnnouncement) {
        await SystemAnnouncementsAPI.updateAnnouncement(
          editingAnnouncement.id,
          announcementData,
        );
        setSuccess("Announcement updated successfully");
      } else {
        await SystemAnnouncementsAPI.createAnnouncement(announcementData);
        setSuccess("Announcement created successfully");
      }

      resetForm();
      fetchAnnouncements();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        "Failed to save announcement: " + (err.message || "Unknown error"),
      );
      console.error("Error saving announcement:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format backend date for the date/time field
   */
  const formatDateForInput = (backendDate) =>
    instantToWallClock(backendDate, ALERT_TZ);

  /**
   * Handle editing an announcement
   */
  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setIsEditing(true);
    setFormData({
      message: announcement.message || "",
      type: announcement.type || "INFO",
      startDate: formatDateForInput(announcement.startDate),
      endDate: formatDateForInput(announcement.endDate),
      priority: announcement.priority || 50,
      targetAudience:
        mapAudienceFromBackend(announcement.targetAudience) || "ALL",
      isActive: announcement.isActive !== false,
      relatedAlertIds: announcement.relatedAlertIds || [],
      relatedTrainingUuids: announcement.relatedTrainingUuids || [],
    });
    setError(null);
    setSuccess(null);
  };

  /**
   * Handle deactivating an announcement
   */
  const handleDeactivate = async (announcementId) => {
    showToastWithAction(
      "Deactivate this announcement? It will be hidden from users but remain in the database.",
      async () => {
        try {
          setLoading(true);
          setError(null);
          await SystemAnnouncementsAPI.deactivateAnnouncement(announcementId);
          setSuccess("Announcement deactivated successfully");
          fetchAnnouncements();
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(
            "Failed to deactivate announcement: " +
              (err.message || "Unknown error"),
          );
          console.error("Error deactivating announcement:", err);
        } finally {
          setLoading(false);
        }
      },
      5,
    );
  };

  /**
   * Handle deleting an announcement permanently
   */
  const handleDelete = async (announcementId) => {
    showToastWithAction(
      "Delete this announcement permanently? This action cannot be undone.",
      async () => {
        try {
          setLoading(true);
          setError(null);
          await SystemAnnouncementsAPI.deleteAnnouncement(announcementId);
          setSuccess("Announcement deleted successfully");
          fetchAnnouncements();
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(
            "Failed to delete announcement: " +
              (err.message || "Unknown error"),
          );
          console.error("Error deleting announcement:", err);
        } finally {
          setLoading(false);
        }
      },
      5,
    );
  };

  /**
   * Format date for display - using centralized DateTimeUtil
   */
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return formatDateTime(dateString);
  };

  /**
   * Format date for display with separate date and time components
   */
  const formatDateSeparate = (dateString) => {
    if (!dateString) return { date: "Not set", time: "" };
    return {
      date: formatDateOnly(dateString, "short"),
      time: formatTimeOnly(dateString, false),
    };
  };

  /**
   * Format date for backend LocalDateTime (yyyy-MM-dd'T'HH:mm:ss)
   */
  const formatDateForBackend = (dateString) =>
    wallClockToNaiveUtc(dateString, ALERT_TZ) || null;

  /**
   * Map frontend audience values to backend enum values
   */
  const mapAudienceToBackend = (frontendAudience) => {
    switch (frontendAudience) {
      case "ALL":
        return "ALL";
      case "AUTHENTICATED":
        return "AUTHENTICATED";
      case "ADMIN":
        return "ADMIN";
      default:
        return "ALL";
    }
  };

  /**
   * Map backend audience enum to frontend values
   */
  const mapAudienceFromBackend = (backendAudience) => {
    switch (backendAudience) {
      case "ALL":
        return "ALL";
      case "AUTHENTICATED":
        return "AUTHENTICATED";
      case "ADMIN":
        return "ADMIN";
      default:
        return "ALL";
    }
  };

  /**
   * Handle sorting by column
   */
  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);
  };

  /**
   * Sort announcements based on current sort field and direction
   */
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case "message":
        aValue = (a.message || "").toLowerCase();
        bValue = (b.message || "").toLowerCase();
        break;
      case "type":
        aValue = a.type || "";
        bValue = b.type || "";
        break;
      case "priority":
        // Priority is now a simple integer (1-100)
        aValue = a.priority || 50;
        bValue = b.priority || 50;
        break;
      case "targetAudience":
        aValue = a.targetAudience || "";
        bValue = b.targetAudience || "";
        break;
      case "startDate":
        aValue = new Date(a.startDate || 0);
        bValue = new Date(b.startDate || 0);
        break;
      case "endDate":
        aValue = new Date(a.endDate || 0);
        bValue = new Date(b.endDate || 0);
        break;
      case "isActive":
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
        break;
      case "id":
      default:
        aValue = a.id || 0;
        bValue = b.id || 0;
        break;
    }

    if (sortDirection === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  /**
   * Get sort indicator for table headers
   */
  const getSortIndicator = (field) => {
    if (sortField !== field) return " ↕"; // Both up and down arrows when not sorted
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="alert-management-container">
      <div className="alert-management-header">
        <h2>Manage Announcements</h2>
        <p>Create and manage platform announcements for users</p>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="alert-message error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="close-btn">
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="alert-message success-message">
          <span>✅ {success}</span>
          <button onClick={() => setSuccess(null)} className="close-btn">
            ×
          </button>
        </div>
      )}

      {/* Create/Edit Announcement Form */}
      <div className="alert-form-section">
        <h3>{isEditing ? "Edit Announcement" : "Create New Announcement"}</h3>
        <form onSubmit={handleSubmit} className="alert-form">
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="message">Announcement Message*</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Enter the announcement message..."
                rows="1"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Announcement Type*</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                {announcementTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                disabled={loading}
              >
                {priorityOptions.map((priority) => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="targetAudience">Target Audience</label>
              <select
                id="targetAudience"
                name="targetAudience"
                value={formData.targetAudience}
                onChange={handleInputChange}
                disabled={loading}
              >
                {audienceOptions.map((audience) => (
                  <option key={audience.value} value={audience.value}>
                    {audience.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group date-field">
              <DateTimeField
                id="startDate"
                label="Starts"
                value={formData.startDate}
                disabled={loading}
                max={formData.endDate}
                maxMessage="the end date"
                allowClear
                presets={["now", "+1d", "+7d"]}
                hint="Leave empty to show the announcement right away."
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, startDate: value }))
                }
              />
            </div>

            <div className="form-group date-field">
              <DateTimeField
                id="endDate"
                label="Ends"
                value={formData.endDate}
                disabled={loading}
                min={formData.startDate}
                minMessage="the start date"
                allowClear
                presetBase={formData.startDate}
                presets={["+1d", "+7d", "+30d"]}
                hint="Leave empty to keep showing it until deactivated."
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, endDate: value }))
                }
              />
            </div>

            <div className="form-group toggle-group">
              <div className="toggle-label">Active</div>
              <label className="toggle-switch" htmlFor="isActive">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Linked Content Section */}
          <div className="linked-content-section">
            <h4>Link to Content (optional)</h4>
            <p className="linked-content-hint">
              Link this announcement to alerts or training modules. Users will
              see action buttons in the banner.
            </p>

            {/* Alert Picker */}
            <div className="form-group linked-picker">
              <label>Linked Alerts</label>
              <select
                className="linked-select"
                value=""
                onChange={(e) => {
                  const alertId = Number(e.target.value);
                  if (alertId && !formData.relatedAlertIds.includes(alertId)) {
                    setFormData((prev) => ({
                      ...prev,
                      relatedAlertIds: [...prev.relatedAlertIds, alertId],
                    }));
                  }
                }}
                disabled={loading}
              >
                <option value="">— Select an alert to link —</option>
                {availableAlerts
                  .filter((a) => !formData.relatedAlertIds.includes(a.id))
                  .map((alert) => (
                    <option key={alert.id} value={alert.id}>
                      #{alert.id} —{" "}
                      {(
                        alert.shortDescription ||
                        alert.message ||
                        ""
                      ).substring(0, 90)}
                    </option>
                  ))}
              </select>
              {formData.relatedAlertIds.length > 0 && (
                <div className="linked-tags">
                  {formData.relatedAlertIds.map((alertId) => {
                    const alert = availableAlerts.find((a) => a.id === alertId);
                    return (
                      <span
                        key={alertId}
                        className="linked-tag linked-tag--alert"
                      >
                        Alert #{alertId}
                        {alert &&
                          `: ${(alert.shortDescription || alert.message || "").substring(0, 40)}`}
                        <button
                          type="button"
                          className="linked-tag-remove"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              relatedAlertIds: prev.relatedAlertIds.filter(
                                (id) => id !== alertId,
                              ),
                            }))
                          }
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Training Picker */}
            <div className="form-group linked-picker">
              <label>Linked Trainings</label>
              <select
                className="linked-select"
                value=""
                onChange={(e) => {
                  const uuid = e.target.value;
                  if (uuid && !formData.relatedTrainingUuids.includes(uuid)) {
                    setFormData((prev) => ({
                      ...prev,
                      relatedTrainingUuids: [
                        ...prev.relatedTrainingUuids,
                        uuid,
                      ],
                    }));
                  }
                }}
                disabled={loading}
              >
                <option value="">— Select a training to link —</option>
                {availableTrainings
                  .filter(
                    (t) => !formData.relatedTrainingUuids.includes(t.uuid),
                  )
                  .map((training) => (
                    <option key={training.uuid} value={training.uuid}>
                      {(training.title || "").substring(0, 90)}
                    </option>
                  ))}
              </select>
              {formData.relatedTrainingUuids.length > 0 && (
                <div className="linked-tags">
                  {formData.relatedTrainingUuids.map((uuid) => {
                    const training = availableTrainings.find(
                      (t) => t.uuid === uuid,
                    );
                    return (
                      <span
                        key={uuid}
                        className="linked-tag linked-tag--training"
                      >
                        {training
                          ? training.title.substring(0, 50)
                          : uuid.substring(0, 8) + "..."}
                        <button
                          type="button"
                          className="linked-tag-remove"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              relatedTrainingUuids:
                                prev.relatedTrainingUuids.filter(
                                  (u) => u !== uuid,
                                ),
                            }))
                          }
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="btn-group">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading
                ? "Saving..."
                : isEditing
                  ? "Update Announcement"
                  : "Create Announcement"}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="btn btn-ghost"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Announcements List */}
      <div className="alerts-list-section">
        <h3>
          Announcements (
          {Array.isArray(announcements) ? announcements.length : 0})
        </h3>
        {loading && (!Array.isArray(announcements) || !announcements.length) ? (
          <LoadingSpinner
            size="sm"
            message="Loading announcements..."
            className="compact"
          />
        ) : !Array.isArray(announcements) || announcements.length === 0 ? (
          <div className="no-alerts-message">No announcements found</div>
        ) : (
          <>
            <div className="alerts-table-container">
              <table className="alerts-table">
                <thead>
                  <tr>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("message")}
                    >
                      Message{getSortIndicator("message")}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("type")}
                    >
                      Type{getSortIndicator("type")}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("priority")}
                    >
                      Priority{getSortIndicator("priority")}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("targetAudience")}
                    >
                      Target{getSortIndicator("targetAudience")}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("startDate")}
                    >
                      Start Date{getSortIndicator("startDate")}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("endDate")}
                    >
                      End Date{getSortIndicator("endDate")}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort("isActive")}
                    >
                      Status{getSortIndicator("isActive")}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(sortedAnnouncements) &&
                    sortedAnnouncements.map((announcement) => (
                      <tr
                        key={announcement.id}
                        className={`alert-row alert-type-${(
                          announcement.type || ""
                        )
                          .toString()
                          .toLowerCase()}`}
                      >
                        <td className="alert-message-cell">
                          <div className="alert-message-text">
                            {announcement.message}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`alert-type-badge type-${(
                              announcement.type || ""
                            )
                              .toString()
                              .toLowerCase()}`}
                          >
                            {announcement.type}
                          </span>
                        </td>
                        <td>
                          <span className="priority-badge">
                            {announcement.priority}
                          </span>
                        </td>
                        <td className="target-audience-cell">
                          {announcement.targetAudience}
                        </td>
                        <td className="date-cell">
                          <div className="date-time-container">
                            <div className="date-part">
                              {formatDateSeparate(announcement.startDate).date}
                            </div>
                            <div className="time-part">
                              {formatDateSeparate(announcement.startDate).time}
                            </div>
                          </div>
                        </td>
                        <td className="date-cell">
                          <div className="date-time-container">
                            <div className="date-part">
                              {formatDateSeparate(announcement.endDate).date}
                            </div>
                            <div className="time-part">
                              {formatDateSeparate(announcement.endDate).time}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              announcement.isActive ? "active" : "inactive"
                            }`}
                          >
                            {announcement.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleEdit(announcement)}
                            disabled={loading}
                            className="btn btn-secondary btn-sm"
                            title="Edit Announcement"
                          >
                            Edit
                          </button>
                          {announcement.isActive && (
                            <button
                              onClick={() => handleDeactivate(announcement.id)}
                              disabled={loading}
                              className="btn btn-ghost btn-sm"
                              title="Deactivate Announcement"
                            >
                              Deactivate
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(announcement.id)}
                            disabled={loading}
                            className="btn btn-destructive btn-sm"
                            title="Delete Announcement"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="alerts-mobile-view">
              {Array.isArray(sortedAnnouncements) &&
                sortedAnnouncements.map((announcement) => (
                  <div key={announcement.id} className="alert-card">
                    <div className="alert-card-header">
                      <div className="alert-card-message">
                        {announcement.message}
                      </div>
                      <span
                        className={`status-badge ${
                          announcement.isActive ? "active" : "inactive"
                        } alert-card-status`}
                      >
                        {announcement.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="alert-card-details">
                      <div className="alert-card-detail">
                        <span className="alert-card-detail-label">Type</span>
                        <span
                          className={`alert-type-badge type-${(
                            announcement.type || ""
                          )
                            .toString()
                            .toLowerCase()}`}
                        >
                          {announcement.type}
                        </span>
                      </div>

                      <div className="alert-card-detail">
                        <span className="alert-card-detail-label">
                          Priority
                        </span>
                        <span className="priority-badge">
                          {announcement.priority}
                        </span>
                      </div>

                      <div className="alert-card-detail">
                        <span className="alert-card-detail-label">Target</span>
                        <span>{announcement.targetAudience}</span>
                      </div>

                      <div className="alert-card-detail">
                        <span className="alert-card-detail-label">
                          Start Date
                        </span>
                        <div className="date-time-container">
                          <div className="date-part">
                            {formatDateSeparate(announcement.startDate).date}
                          </div>
                          <div className="time-part">
                            {formatDateSeparate(announcement.startDate).time}
                          </div>
                        </div>
                      </div>

                      <div className="alert-card-detail">
                        <span className="alert-card-detail-label">
                          End Date
                        </span>
                        <div className="date-time-container">
                          <div className="date-part">
                            {formatDateSeparate(announcement.endDate).date}
                          </div>
                          <div className="time-part">
                            {formatDateSeparate(announcement.endDate).time}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="alert-card-actions">
                      <button
                        onClick={() => handleEdit(announcement)}
                        disabled={loading}
                        className="btn btn-secondary btn-sm"
                        title="Edit Announcement"
                      >
                        Edit
                      </button>
                      {announcement.isActive && (
                        <button
                          onClick={() => handleDeactivate(announcement.id)}
                          disabled={loading}
                          className="btn btn-ghost btn-sm"
                          title="Deactivate Announcement"
                        >
                          Deactivate
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        disabled={loading}
                        className="btn btn-destructive btn-sm"
                        title="Delete Announcement"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AlertManagement;
